import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/services/auth';
import { aiSettingsService } from '@/services/aiSettings';
import i18n from '@/i18n/config';
import type { SupportedLanguage } from '@/i18n/config';
import { UserInfo } from '@/types';

/**
 * Load user's language preference from database.
 * Called after successful authentication (login or token restore).
 */
async function loadLanguagePreference(): Promise<void> {
  try {
    const config = await aiSettingsService.getConfig();
    if (config.preferred_language && config.preferred_language !== i18n.language) {
      await i18n.changeLanguage(config.preferred_language as SupportedLanguage);
      console.log(`[AuthContext] Language loaded: ${config.preferred_language}`);
    }
  } catch (error) {
    // Non-critical - log but don't fail auth
    console.debug('[AuthContext] Could not load language preference:', error);
  }
}

interface AuthContextType {
  user: UserInfo | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperuser: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on mount
  useEffect(() => {
    console.log('[AuthContext] useEffect RUNNING - initAuth starting');

    const initAuth = async () => {
      const storedToken = authService.getToken();
      console.log('[AuthContext] initAuth - stored token exists:', !!storedToken);

      if (storedToken) {
        console.log('[AuthContext] initAuth - setting token to state');
        setToken(storedToken);
        try {
          console.log('[AuthContext] initAuth - fetching current user...');
          const userInfo = await authService.getCurrentUser();
          console.log('[AuthContext] initAuth - user fetched successfully:', userInfo.username);
          setUser(userInfo);
          // Load language preference after successful auth restore
          await loadLanguagePreference();
        } catch (error) {
          console.error('[AuthContext] initAuth - FAILED to fetch user info:', error);
          console.error('[AuthContext] initAuth - CLEARING token due to error');
          authService.removeToken();
          setToken(null);
        }
      }

      console.log('[AuthContext] initAuth - setting isLoading to false');
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (newToken: string) => {
    console.log('[AuthContext] login() called with token');
    setIsLoading(true);
    try {
      console.log('[AuthContext] login() - saving token to localStorage');
      authService.saveToken(newToken);
      console.log('[AuthContext] login() - setting token to state');
      setToken(newToken);

      console.log('[AuthContext] login() - fetching current user...');
      const userInfo = await authService.getCurrentUser();
      console.log('[AuthContext] login() - user fetched:', userInfo.username);
      setUser(userInfo);
      // Load language preference after successful login
      await loadLanguagePreference();
      console.log('[AuthContext] login() - SUCCESS - isAuthenticated should now be true');
    } catch (error) {
      console.error('[AuthContext] login() - ERROR:', error);
      console.error('[AuthContext] login() - CLEARING all auth data');
      authService.removeToken();
      setToken(null);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log('[AuthContext] logout() called');
    authService.removeToken();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!token) return;

    try {
      const userInfo = await authService.getCurrentUser();
      setUser(userInfo);
    } catch (error) {
      console.error('Failed to refresh user info:', error);
      logout();
    }
  };

  const isAuthenticated = !!token && !!user;
  const isSuperuser = !!user?.is_superuser;

  // Log state changes
  useEffect(() => {
    console.log('[AuthContext] STATE CHANGE:', {
      hasToken: !!token,
      hasUser: !!user,
      username: user?.username,
      isAuthenticated,
      isSuperuser,
      isLoading,
    });
  }, [token, user, isAuthenticated, isSuperuser, isLoading]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    isSuperuser,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
